import axios from 'axios';
import { Preferences } from '@capacitor/preferences';
import { CheckAgeSignalsResult, RuntimeEnvironmentResult } from 'insight-age-check';

import { AGE_ASSURANCE_DEBUG_ENABLED, apiURI, appVersion, ENVIRONMENT } from './environment';
import signReq from './signRequest';

const telemetryQueueKey = 'ageAssuranceTelemetryQueue';
const maxQueuedEvents = 10;

type NullableString = string | null;
type NullableNumber = number | null;
type NullableBoolean = boolean | null;

export interface AgeAssuranceTelemetryEvent {
  event_type: 'launch_result' | 'api_error';
  launch_run_id: string;
  app_name: string;
  app_environment: string;
  platform: string;
  os_version?: string;
  occurred_at: string;
  allowed?: NullableBoolean;
  behavior: string;
  ui_message_key?: NullableString;
  ui_message_text?: NullableString;
  source?: NullableString;
  required_features?: string[];
  user_status?: NullableString;
  age_band?: NullableString;
  age_lower?: NullableNumber;
  age_upper?: NullableNumber;
  age_range_declaration?: NullableString;
  parental_controls?: string[];
  sharing_declined?: NullableBoolean;
  approval_path?: NullableString;
  approval_result?: NullableString;
  api_name?: NullableString;
  api_code?: NullableString;
  api_message?: NullableString;
  elapsed_ms?: NullableNumber;
  raw_payload?: Record<string, unknown>;
  debug_telemetry_enabled?: boolean;
}

interface PostableAgeAssuranceTelemetryEvent extends AgeAssuranceTelemetryEvent {
  uuid: string;
  version: string;
  timestamp: string;
  sig: string;
}

const nonProductionApiAuth = ENVIRONMENT !== 'production'
  ? { auth: { username: 'igins', password: 'llc' } }
  : {};

export const createAgeAssuranceTelemetryRunId = () => {
  if (window.crypto && 'randomUUID' in window.crypto) {
    return window.crypto.randomUUID();
  }

  return `age-gate-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const sanitizeRawPayload = (value: unknown): Record<string, unknown> | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(JSON.stringify(value, (key, nestedValue) => {
      if (key === 'installId') {
        return undefined;
      }

      return nestedValue;
    }));
  } catch (error) {
    return {
      serializationError: error instanceof Error ? error.message : 'Unable to serialize payload.',
    };
  }
};

const getProductionBehavior = (event: AgeAssuranceTelemetryEvent) => {
  if (event.event_type === 'api_error') {
    return 'api_error';
  }

  if (event.behavior === 'show_retry_error' || event.ui_message_key === 'availability_check_failed') {
    return 'availability_check_failed';
  }

  if (event.allowed === true) {
    return 'allowed';
  }

  if (event.allowed === false) {
    return 'blocked';
  }

  return 'unknown';
};

const getProductionMessageKey = (event: AgeAssuranceTelemetryEvent) => {
  if (event.behavior === 'show_retry_error' || event.ui_message_key === 'availability_check_failed') {
    return 'availability_check_failed';
  }

  if (event.allowed === false) {
    return 'blocked';
  }

  return null;
};

const getProductionSource = (event: AgeAssuranceTelemetryEvent) => (
  event.event_type === 'api_error' ? 'age_assurance_api' : 'age_assurance_launch'
);

const getPostableTelemetryEvent = (event: AgeAssuranceTelemetryEvent): AgeAssuranceTelemetryEvent => {
  if (AGE_ASSURANCE_DEBUG_ENABLED) {
    return {
      ...event,
      debug_telemetry_enabled: true,
      raw_payload: sanitizeRawPayload(event.raw_payload),
    };
  }

  return {
    event_type: event.event_type,
    launch_run_id: event.launch_run_id,
    app_name: event.app_name,
    app_environment: event.app_environment,
    platform: event.platform,
    os_version: event.os_version,
    occurred_at: event.occurred_at,
    allowed: event.allowed ?? null,
    behavior: getProductionBehavior(event),
    ui_message_key: getProductionMessageKey(event),
    ui_message_text: null,
    source: getProductionSource(event),
    api_name: event.event_type === 'api_error' ? 'age_assurance' : null,
    api_code: event.event_type === 'api_error' ? event.api_code ?? null : null,
    api_message: null,
    elapsed_ms: event.elapsed_ms ?? null,
    debug_telemetry_enabled: false,
  };
};

export const getAgeAssuranceAgeBand = (ageSignalResult?: CheckAgeSignalsResult | null) => {
  if (!ageSignalResult) {
    return null;
  }

  const lower = ageSignalResult.ageLower;
  const upper = ageSignalResult.ageUpper;

  if (lower !== undefined && lower >= 18) {
    return '18_plus';
  }

  if (upper !== undefined && upper < 13) {
    return 'under_13';
  }

  if (lower !== undefined && lower >= 16 && upper !== undefined && upper <= 17) {
    return '16_17';
  }

  if (lower !== undefined && lower >= 13 && upper !== undefined && upper <= 15) {
    return '13_15';
  }

  if (lower !== undefined || upper !== undefined) {
    return 'age_range_other';
  }

  return null;
};

export const getAgeAssuranceSignalFields = (
  ageSignalResult?: CheckAgeSignalsResult | null,
) => {
  if (!ageSignalResult) {
    return {};
  }

  return {
    user_status: ageSignalResult.userStatus ?? null,
    age_band: getAgeAssuranceAgeBand(ageSignalResult),
    age_lower: ageSignalResult.ageLower ?? null,
    age_upper: ageSignalResult.ageUpper ?? null,
    age_range_declaration: ageSignalResult.ageRangeDeclaration ?? null,
    parental_controls: ageSignalResult.activeParentalControls ?? [],
    sharing_declined: ageSignalResult.sharingDeclined ?? null,
  };
};

const getQueuedEvents = async (): Promise<AgeAssuranceTelemetryEvent[]> => {
  const { value } = await Preferences.get({
    key: telemetryQueueKey,
  });

  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const setQueuedEvents = async (events: AgeAssuranceTelemetryEvent[]) => {
  if (!events.length) {
    await Preferences.remove({
      key: telemetryQueueKey,
    });
    return;
  }

  await Preferences.set({
    key: telemetryQueueKey,
    value: JSON.stringify(events.slice(-maxQueuedEvents)),
  });
};

const queueEvent = async (event: AgeAssuranceTelemetryEvent) => {
  const queuedEvents = await getQueuedEvents();
  queuedEvents.push(getPostableTelemetryEvent(event));
  await setQueuedEvents(queuedEvents);
};

const postTelemetryEvent = async (
  uuid: string,
  event: AgeAssuranceTelemetryEvent,
) => {
  const timestamp = Math.round(Date.now() / 1000).toString();
  const telemetryEvent = getPostableTelemetryEvent(event);
  const body: PostableAgeAssuranceTelemetryEvent = {
    ...telemetryEvent,
    uuid,
    version: appVersion,
    timestamp,
    sig: signReq(uuid, timestamp),
  };
  const url = `${apiURI}/age_assurance/register`;

  const response = await axios.post(
    url,
    body,
    nonProductionApiAuth,
  );

  if (response.data?.status && Number(response.data.status) >= 400) {
    throw new Error(response.data.status_message ?? 'Age assurance telemetry API rejected the event.');
  }
};

let isFlushingQueue = false;

export const flushQueuedAgeAssuranceTelemetry = async (uuid: string) => {
  if (!uuid || isFlushingQueue) {
    return;
  }

  isFlushingQueue = true;

  try {
    const queuedEvents = await getQueuedEvents();
    const remainingEvents: AgeAssuranceTelemetryEvent[] = [];

    for (const event of queuedEvents) {
      try {
        await postTelemetryEvent(uuid, event);
      } catch {
        remainingEvents.push(event);
      }
    }

    await setQueuedEvents(remainingEvents);
  } finally {
    isFlushingQueue = false;
  }
};

export const trackAgeAssuranceTelemetry = async (
  uuid: string,
  event: Omit<AgeAssuranceTelemetryEvent, 'occurred_at'> & { occurred_at?: string },
) => {
  if (!uuid) {
    return;
  }

  const eventWithTimestamp: AgeAssuranceTelemetryEvent = {
    ...event,
    occurred_at: event.occurred_at ?? new Date().toISOString(),
  };

  try {
    await flushQueuedAgeAssuranceTelemetry(uuid);
    await postTelemetryEvent(uuid, eventWithTimestamp);
  } catch (error) {
    console.warn('[AgeGateTelemetry] queueing age assurance telemetry after send failure', error);
    await queueEvent(eventWithTimestamp);
  }
};

export const getRuntimeEnvironmentTelemetryPayload = (
  runtimeEnvironment?: RuntimeEnvironmentResult | null,
) => (
  runtimeEnvironment ? { runtimeEnvironment } : {}
);
